import React from 'react';
import Icon from '../../../components/AppIcon';

const CalculationPanel = ({ calculationData }) => {
  if (!calculationData) {
    return (
      <div className="bg-muted/30 border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="Calculator" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No Calculation Yet</h3>
          <p className="text-sm text-muted-foreground">
            Fill in the form details to see automatic fee calculations
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('bn-BD')?.format(number);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="TrendingUp" size={20} className="text-success" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-foreground">Fee Calculation</h3>
          <p className="text-sm text-muted-foreground">Real-time revenue breakdown</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Store Information */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="Store" size={16} />
            Store Details
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Location:</span>
              <p className="font-medium text-foreground">{calculationData?.storeDetails?.label}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Monthly Rent:</span>
              <p className="font-medium text-foreground">{formatCurrency(calculationData?.storeDetails?.rent)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Store Size:</span>
              <p className="font-medium text-foreground">{formatNumber(calculationData?.storeDetails?.size)} sq ft</p>
            </div>
            <div>
              <span className="text-muted-foreground">Rent per Sq Ft:</span>
              <p className="font-medium text-foreground">{formatCurrency(calculationData?.rentPerSqFt)}</p>
            </div>
          </div>
        </div>

        {/* Rate Calculation */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="Calculator" size={16} />
            Rate Calculation Formula
          </h4>
          
          <div className="bg-background border border-border rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Base Rate ({calculationData?.rateTypeDetails?.label}):</span>
              <span className="font-medium">{formatCurrency(calculationData?.rateTypeDetails?.baseRate)}</span>
            </div>
            
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Location Multiplier:</span>
              <span className="font-medium">×{calculationData?.locationMultiplier}</span>
            </div>
            
            <div className="border-t border-border pt-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-foreground">Unit Rate:</span>
                <span className="font-semibold text-lg text-primary">{formatCurrency(calculationData?.unitRate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Calculation */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Icon name="Receipt" size={16} />
            Total Revenue Breakdown
          </h4>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Unit Rate:</span>
              <span className="font-medium">{formatCurrency(calculationData?.unitRate)}</span>
            </div>
            
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Quantity:</span>
              <span className="font-medium">×{formatNumber(calculationData?.quantity)}</span>
            </div>
            
            <div className="border-t border-primary/20 pt-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-foreground">Total Fees:</span>
                <span className="text-2xl font-bold text-primary">{formatCurrency(calculationData?.totalFees)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={16} className="text-accent mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Calculation Notes:</p>
              <ul className="text-muted-foreground space-y-1 text-xs">
                <li>• Location multiplier based on rent per sq ft (High: 1.3x, Medium: 1.1x, Low: 1.0x)</li>
                <li>• Prices are in Bangladeshi Taka (BDT)</li>
                <li>• Final fees may vary based on installation complexity</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationPanel;